/**
 * Not Found Redirect Component
 * 
 * Handles 404 errors by showing a toaster message and redirecting
 * to the appropriate frontpage using the root URL resolver.
 */

import React, {useEffect} from 'react';
import {useStoreState} from '@/store';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';
import CellLoader from '@/components/loading/CellLoader';
interface NotFoundRedirectProps {
  /** Custom message to show in the toaster */
  message?: string;
  /** Delay before redirect (in milliseconds) */
  redirectDelay?: number;
  /** Show debug information in development */
  showDebugInfo?: boolean;
}

export const NotFoundRedirect: React.FC<NotFoundRedirectProps> = ({
    message,
    redirectDelay = 2000,
    showDebugInfo = false,
}) => {
    const {showWarning} = useAlert();
  
    // Get user data for debug info (but don't use in useEffect dependencies)
    const userData = useStoreState(state => state.userData.data);
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);

    useEffect(() => {
        console.error('🚨 NotFoundRedirect: Component mounted for path:', window.location.pathname);
        console.error('🚨 NotFoundRedirect: userData:', userData);
        console.error('🚨 NotFoundRedirect: menuStructure length:', menuStructure?.length);
    
        // Show toaster message once
        const errorMessage = message || i18n.t('page_not_found_redirecting.text', 'Page not found. Redirecting to dashboard...');
        showWarning(errorMessage);

        // Redirect after delay
        const redirectTimer = setTimeout(async () => {
            try {
                console.error('🚨 NotFoundRedirect: Starting redirect process...');
                console.error('🚨 NotFoundRedirect: Current pathname:', window.location.pathname);
        
                // Get current userData and menuStructure at redirect time
                const currentUserData = userData;
                const currentMenuStructure = menuStructure;
        
                console.error('🚨 NotFoundRedirect: Using userData:', currentUserData);
                console.error('🚨 NotFoundRedirect: Using menuStructure length:', currentMenuStructure?.length);
        
                // Try to get root URL
                const {getRootRedirectUrl} = await import('../../utils/rootUrlResolver');
                const rootUrl = getRootRedirectUrl({ 
                    userData: currentUserData, 
                    menuStructure: currentMenuStructure 
                });
        
                console.error('🚨 NotFoundRedirect: Root URL resolved to:', rootUrl);
        
                // Check for potential redirect loops
                const currentPath = window.location.pathname;
                const isLoop = currentPath === rootUrl || 
                      rootUrl.includes('not-found') || 
                      rootUrl.includes('error') ||
                      rootUrl === currentPath;
        
                console.error('🚨 NotFoundRedirect: Loop detection:', {
                    currentPath,
                    rootUrl,
                    isLoop
                });
        
                if (isLoop) {
                    console.error('🚨 NotFoundRedirect: LOOP DETECTED! Using hard fallback');
                    window.location.href = '/app/discover/my-stories';
                } else {
                    console.error('🚨 NotFoundRedirect: No loop detected, redirecting to:', rootUrl);
                    window.location.href = rootUrl;
                }
            } catch (error) {
                console.error('🚨 NotFoundRedirect: Error during redirect:', error);
                console.error('🚨 NotFoundRedirect: Using emergency fallback');
                window.location.href = '/app/discover/my-stories';
            }
        }, redirectDelay);

        return () => {
            console.error('🚨 NotFoundRedirect: Component unmounting, clearing timer');
            clearTimeout(redirectTimer);
        };
    }, []); // Empty dependency array to prevent re-renders

    if (showDebugInfo) {
        return (
            <div style={{padding: '2rem', backgroundColor: '#fff3cd', border: '2px solid #856404'}}>
                <h2>🚨 Page Not Found</h2>
                <p><strong>Current path:</strong> {window.location.pathname}</p>
                <p><strong>Redirecting in:</strong> {redirectDelay / 1000} seconds</p>
                <p><strong>User authenticated:</strong> {userData ? 'Yes' : 'No'}</p>
                <p><strong>Menu structure loaded:</strong> {menuStructure?.length > 0 ? 'Yes' : 'No'}</p>
                <div style={{marginTop: '1rem'}}>
                    <CellLoader fullHeight={true} />
                </div>
            </div>
        );
    }

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '200px',
            padding: '2rem' 
        }}>
            <h3 style={{marginBottom: '1rem', color: '#6c757d'}}>
                {i18n.t('page_not_found.text', 'Page Not Found')}
            </h3>
            <p style={{marginBottom: '2rem', color: '#6c757d'}}>
                {i18n.t('redirecting_to_dashboard.text', 'Redirecting to dashboard...')}
            </p>
            <CellLoader fullHeight={true} />
        </div>
    );
};

export default NotFoundRedirect;